import { OrderQuoteStatus } from '@/lib/enums/OrderQuoteStatus'
import { TransactionLineType } from '@/lib/enums/TransactionLineType'
import { parseCurrency } from '@/lib/utils'

type UnknownRecord = Record<string, unknown>

// The cart fields these helpers read. order_quote (OrderQuoteService) is read defensively, so it stays
// unknown here and is narrowed where it is used.
export interface OrderQuoteCart {
  id?: number | string | null
  order_quote?: unknown
}

type OrderQuoteCartInput = OrderQuoteCart | null | undefined

export interface OrderQuoteLockControls {
  editable?: boolean
  locked?: boolean
  lock_reason?: string | null
  can_complete_order?: boolean
  can_refund_payments?: boolean
  can_save_for_later?: boolean
  can_unsave?: boolean
  can_delete?: boolean
  holds_customer_money?: boolean
}

/** The quote controls getOrderQuoteLockState reads, for callers that type their cart. */
export interface OrderQuoteLockInput {
  id?: number
  order_quote?: { controls?: OrderQuoteLockControls | null } | null
}

export interface OrderQuoteLockState {
  editable: boolean
  locked: boolean
  lockReason: string | null
  isPreparedPosPaymentSessionLock: boolean
  /** The cart became an order the sale finished; a placement still running isn't one. */
  isFinalizedSale: boolean
  paymentModalReachable: boolean
  canCompleteOrder?: boolean
  canRefundPayments?: boolean
  canSaveForLater?: boolean
  canUnsave?: boolean
  canDelete?: boolean
  holdsCustomerMoney?: boolean
}

interface OrderQuoteIssueContext {
  cartId?: number | string | null
  id?: number | string | null
  issueKey?: string
  fields?: string
  [key: string]: unknown
}

export interface OrderQuoteFinancials {
  subtotal: number | null
  adjustmentsTotal: number | null
  deliveryFee: number | null
  shippingFee: number | null
  tipAmount: number | null
  relayFee: number | null
  tax: number | null
  total: number | null
  paidTotal: number | null
  coveredTotal: number | null
  remainingTotal: number | null
  remainingCoveredTotal: number | null
  customerTotal: number | null
  customerTax: number | null
  customerPaidTotal: number | null
  hasKnownTotals: boolean
}

const warnedOrderQuoteIssues = new Set<string>()

export const preparedPosPaymentSessionLockReason = 'prepared_pos_payment_session'

/** An order change draft held as priced while a payment toward it is open on the order (OrderChangeDraftPolicy). */
export const orderChangePaymentLockReason = 'order_change_payment_in_progress'

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

export function parseOrderQuoteMoney(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? parseCurrency(value) : null
  if (typeof value !== 'string' || !/^-?(?:\d+(?:\.\d*)?|\.\d+)$/.test(value.trim())) {
    return null
  }
  return parseCurrency(value)
}

export function getOrderQuote(cart: OrderQuoteCartInput): UnknownRecord | null {
  const quote = cart?.order_quote

  return isRecord(quote) ? quote : null
}

export function hasOrderQuoteContract(cart: OrderQuoteCartInput): boolean {
  return Boolean(cart && Object.prototype.hasOwnProperty.call(cart, 'order_quote'))
}

export function getOrderQuoteComponent(
  cart: OrderQuoteCartInput,
  type: string
): UnknownRecord | null {
  return (
    asArray(getOrderQuote(cart)?.components).find(
      (component): component is UnknownRecord => isRecord(component) && component.type === type
    ) ?? null
  )
}

export function getOrderQuoteComponentAmount(
  cart: OrderQuoteCartInput,
  type: string,
  key = 'net_amount'
): number | null {
  return parseOrderQuoteMoney(getOrderQuoteComponent(cart, type)?.[key])
}

export function getOrderQuoteComponentControls(
  cart: OrderQuoteCartInput,
  type: string
): UnknownRecord | null {
  const controls = getOrderQuoteComponent(cart, type)?.controls

  return isRecord(controls) ? controls : null
}

export function getOrderQuoteControls(cart: OrderQuoteCartInput): UnknownRecord | null {
  const controls = getOrderQuote(cart)?.controls

  return isRecord(controls) ? controls : null
}

export function warnOrderQuoteContractIssue(
  message: string,
  context: OrderQuoteIssueContext = {}
): void {
  const warningKey = [
    message,
    context.cartId ?? context.id ?? 'unknown',
    context.issueKey ?? context.fields ?? '',
  ].join(':')

  if (warnedOrderQuoteIssues.has(warningKey)) {
    return
  }

  warnedOrderQuoteIssues.add(warningKey)
  console.warn(`[order_quote] ${message}`, context)
}

function missingRelayFeeControls() {
  return {
    available: false,
    enabled: false,
    editable: false,
    locked: true,
    lockReason: 'order_quote_missing_relay_fee_controls',
    deliveryServiceabilityStatus: null,
  }
}

export function getOrderQuoteRelayFeeControls(cart: OrderQuoteCartInput) {
  if (!cart) {
    return missingRelayFeeControls()
  }

  const controls = getOrderQuoteComponentControls(cart, TransactionLineType.RelayFee.Value)

  if (!controls || !Object.prototype.hasOwnProperty.call(controls, 'available')) {
    warnOrderQuoteContractIssue('Relay Fee controls are missing from order_quote.', {
      cartId: cart?.id ?? null,
    })

    return missingRelayFeeControls()
  }

  return {
    available: Boolean(controls.available),
    enabled: Boolean(controls.enabled),
    editable: Boolean(controls.editable),
    locked: Boolean(controls.locked),
    lockReason: optionalString(controls.lock_reason),
    deliveryServiceabilityStatus: optionalString(controls.delivery_serviceability_status),
  }
}

export function getOrderQuoteDeliveryControls(cart: OrderQuoteCartInput) {
  const controls = getOrderQuoteComponentControls(cart, TransactionLineType.Delivery.Value)

  if (!controls) {
    // Before the first item there is no cart, so no quote to read; only a cart without one is a contract issue.
    if (cart) {
      warnOrderQuoteContractIssue('Delivery controls are missing from order_quote.', {
        cartId: cart.id ?? null,
      })
    }

    return {
      overridden: false,
      overrideAmount: null,
      editable: false,
      locked: true,
      lockReason: 'order_quote_missing_delivery_controls',
    }
  }

  return {
    overridden: Boolean(controls.overridden),
    overrideAmount: parseOrderQuoteMoney(controls.override_amount),
    editable: Boolean(controls.editable),
    locked: Boolean(controls.locked),
    lockReason: optionalString(controls.lock_reason),
  }
}

export function getOrderQuoteTipControls(cart: OrderQuoteCartInput) {
  const controls = getOrderQuoteComponentControls(cart, TransactionLineType.Tip.Value)

  if (!controls) {
    if (cart) {
      warnOrderQuoteContractIssue('Tip controls are missing from order_quote.', {
        cartId: cart.id ?? null,
      })
    }

    return {
      type: null,
      value: null,
      editable: false,
      locked: true,
      lockReason: 'order_quote_missing_tip_controls',
    }
  }

  return {
    type: optionalString(controls.type),
    value: parseOrderQuoteMoney(controls.value),
    editable: Boolean(controls.editable),
    locked: Boolean(controls.locked),
    lockReason: optionalString(controls.lock_reason),
  }
}

export function getOrderQuoteCartControls(cart: OrderQuoteCartInput) {
  const controls = getOrderQuoteControls(cart)

  if (!controls) {
    if (cart) {
      warnOrderQuoteContractIssue('Cart controls are missing from order_quote.', {
        cartId: cart.id ?? null,
      })
    }

    return {
      editable: false,
      locked: true,
      lockReason: 'order_quote_missing_cart_controls',
    }
  }

  return {
    editable: Boolean(controls.editable),
    locked: Boolean(controls.locked),
    lockReason: optionalString(controls.lock_reason),
  }
}

export function getOrderQuotePricingAdjustmentControls(cart: OrderQuoteCartInput) {
  const controls = getOrderQuoteControls(cart)?.pricing_adjustments

  if (!isRecord(controls)) {
    if (cart) {
      warnOrderQuoteContractIssue('Pricing adjustment controls are missing from order_quote.', {
        cartId: cart.id ?? null,
      })
    }

    return {
      editable: false,
      locked: true,
      lockReason: 'order_quote_missing_pricing_adjustment_controls',
    }
  }

  return {
    editable: Boolean(controls.editable),
    locked: Boolean(controls.locked),
    lockReason: optionalString(controls.lock_reason),
  }
}

export function getOrderQuoteLockState(cart: OrderQuoteCartInput): OrderQuoteLockState {
  if (!cart) {
    return {
      editable: false,
      locked: false,
      lockReason: null,
      isPreparedPosPaymentSessionLock: false,
      isFinalizedSale: false,
      paymentModalReachable: true,
    }
  }

  const controls = getOrderQuoteCartControls(cart)
  const quoteControls = getOrderQuoteControls(cart)
  const locked = controls.locked
  const lockReason = controls.lockReason
  const isPreparedPosPaymentSessionLock =
    locked && lockReason === preparedPosPaymentSessionLockReason

  return {
    editable: controls.editable,
    locked,
    lockReason,
    isPreparedPosPaymentSessionLock,
    isFinalizedSale: locked && lockReason === 'finalized_sales_order',
    paymentModalReachable: !locked || isPreparedPosPaymentSessionLock,
    canCompleteOrder: quoteControls?.can_complete_order === true,
    canRefundPayments: quoteControls?.can_refund_payments === true,
    canSaveForLater: quoteControls?.can_save_for_later === true,
    canUnsave: quoteControls?.can_unsave === true,
    canDelete: quoteControls?.can_delete === true,
    holdsCustomerMoney: quoteControls?.holds_customer_money === true,
  }
}

export function isPreparedPosPaymentSessionOrderQuoteLock(cart: OrderQuoteCartInput): boolean {
  return getOrderQuoteLockState(cart).isPreparedPosPaymentSessionLock
}

export function isOrderQuoteLocked(cart: OrderQuoteCartInput): boolean {
  return getOrderQuoteLockState(cart).locked
}

export function isOrderQuoteEditable(cart: OrderQuoteCartInput): boolean {
  return getOrderQuoteLockState(cart).editable
}

export function getOrderQuoteSubscription(cart: OrderQuoteCartInput): UnknownRecord | null {
  const subscription = getOrderQuote(cart)?.subscription

  return isRecord(subscription) ? subscription : null
}

export function getOrderQuoteReadinessSection(
  cart: OrderQuoteCartInput,
  section: string
): UnknownRecord | null {
  const readiness = getOrderQuote(cart)?.readiness
  const readinessSection = isRecord(readiness) ? readiness[section] : null

  return isRecord(readinessSection) ? readinessSection : null
}

export function getOrderQuoteFulfillment(cart: OrderQuoteCartInput): UnknownRecord | null {
  const fulfillment = getOrderQuote(cart)?.fulfillment

  return isRecord(fulfillment) ? fulfillment : null
}

export function getOrderQuoteFulfillmentType(cart: OrderQuoteCartInput): unknown {
  return getOrderQuoteFulfillment(cart)?.type ?? null
}

export function orderQuoteHasSubscription(cart: OrderQuoteCartInput): boolean {
  return getOrderQuoteSubscription(cart)?.required === true
}

export function getOrderQuoteSubscriptionCompositionType(cart: OrderQuoteCartInput): string | null {
  const compositionType = getOrderQuoteSubscription(cart)?.composition_type

  return typeof compositionType === 'string' && compositionType.trim() !== ''
    ? compositionType
    : null
}

export function isOrderQuoteSubscriptionOnly(cart: OrderQuoteCartInput): boolean {
  return getOrderQuoteSubscription(cart)?.is_subscription_only === true
}

export function getOrderQuoteSubscriptionTerms(cart: OrderQuoteCartInput): UnknownRecord | null {
  const terms = getOrderQuoteSubscription(cart)?.terms

  return isRecord(terms) ? terms : null
}

export function getOrderQuoteSubscriptionCustomer(cart: OrderQuoteCartInput): UnknownRecord | null {
  const customer = getOrderQuoteSubscription(cart)?.customer

  return isRecord(customer) ? customer : null
}

const subscriptionPaymentBlockingReasons = new Set([
  'subscription_customer_required',
  'subscription_fulfillment_required',
])

function isSubscriptionPaymentBlockingReason(reason: unknown): boolean {
  return typeof reason === 'string' && subscriptionPaymentBlockingReasons.has(reason)
}

export function getOrderQuoteSubscriptionPaymentBlocker(cart: OrderQuoteCartInput): string | null {
  const subscription = getOrderQuoteSubscription(cart)

  if (!subscription || subscription.required !== true) {
    return null
  }

  const readiness = getOrderQuoteReadinessSection(cart, 'subscription')
  const readinessMessages = asArray(readiness?.messages)
  const firstReadinessMessage = readinessMessages[0]
  const customer = isRecord(subscription.customer) ? subscription.customer : null
  const fulfillment = isRecord(subscription.fulfillment) ? subscription.fulfillment : null
  const message =
    [...readinessMessages, ...asArray(subscription.messages)].find(
      (candidate): candidate is UnknownRecord =>
        isRecord(candidate) && isSubscriptionPaymentBlockingReason(candidate.code)
    )?.message ??
    (isSubscriptionPaymentBlockingReason(readiness?.reason) && isRecord(firstReadinessMessage)
      ? firstReadinessMessage.message
      : null) ??
    (isSubscriptionPaymentBlockingReason(customer?.reason) ? customer?.message : null) ??
    (isSubscriptionPaymentBlockingReason(fulfillment?.reason) ? fulfillment?.message : null) ??
    null

  return typeof message === 'string' && message.trim() !== '' ? message : null
}

export function getOrderQuoteSubscriptionTermsCurrent(cart: OrderQuoteCartInput): unknown {
  return getOrderQuoteSubscriptionTerms(cart)?.current ?? null
}

export function getOrderQuoteSubscriptionTermsAcceptance(cart: OrderQuoteCartInput): unknown {
  return getOrderQuoteSubscriptionTerms(cart)?.acceptance ?? null
}

export function areOrderQuoteSubscriptionTermsAccepted(cart: OrderQuoteCartInput): boolean {
  const acceptance = getOrderQuoteSubscriptionTermsAcceptance(cart)

  return isRecord(acceptance) && Boolean(acceptance.accepted_at)
}

export function requiresOrderQuoteSubscriptionTerms(cart: OrderQuoteCartInput): boolean {
  return orderQuoteHasSubscription(cart) && !areOrderQuoteSubscriptionTermsAccepted(cart)
}

function getOrderQuoteTotals(cart: OrderQuoteCartInput): UnknownRecord | null {
  const totals = getOrderQuote(cart)?.totals

  return isRecord(totals) ? totals : null
}

export function getOrderQuoteTotal(cart: OrderQuoteCartInput, key = 'total'): number | null {
  return parseOrderQuoteMoney(getOrderQuoteTotals(cart)?.[key])
}

export function hasKnownOrderQuoteTotals(cart: OrderQuoteCartInput): boolean {
  return (
    getOrderQuote(cart)?.status === OrderQuoteStatus.Known.Value &&
    getOrderQuoteTotals(cart)?.status === OrderQuoteStatus.Known.Value &&
    getOrderQuoteTotal(cart, 'total') !== null
  )
}

export function hasPendingOrderQuoteTotals(cart: OrderQuoteCartInput): boolean {
  return (
    getOrderQuote(cart)?.status === OrderQuoteStatus.Pending.Value ||
    getOrderQuoteTotals(cart)?.status === OrderQuoteStatus.Pending.Value
  )
}

export function getOrderQuoteFinancials(cart: OrderQuoteCartInput): OrderQuoteFinancials {
  return {
    subtotal: getOrderQuoteComponentAmount(cart, TransactionLineType.Product.Value, 'gross_amount'),
    adjustmentsTotal: getOrderQuoteTotal(cart, 'adjustments_total'),
    deliveryFee: getOrderQuoteComponentAmount(
      cart,
      TransactionLineType.Delivery.Value,
      'gross_amount'
    ),
    shippingFee: getOrderQuoteComponentAmount(cart, TransactionLineType.Shipping.Value),
    tipAmount: getOrderQuoteComponentAmount(cart, TransactionLineType.Tip.Value),
    relayFee: getOrderQuoteComponentAmount(cart, TransactionLineType.RelayFee.Value),
    tax: getOrderQuoteComponentAmount(cart, TransactionLineType.Tax.Value),
    total: getOrderQuoteTotal(cart, 'total'),
    paidTotal: getOrderQuoteTotal(cart, 'paid_total'),
    customerTotal: getOrderQuoteTotal(cart, 'customer_total'),
    customerTax: getOrderQuoteTotal(cart, 'customer_tax'),
    customerPaidTotal: getOrderQuoteTotal(cart, 'customer_paid_total'),
    coveredTotal: getOrderQuoteTotal(cart, 'covered_total'),
    remainingTotal: getOrderQuoteTotal(cart, 'remaining_total'),
    remainingCoveredTotal: getOrderQuoteTotal(cart, 'remaining_covered_total'),
    hasKnownTotals: hasKnownOrderQuoteTotals(cart),
  }
}

export function getOrderQuoteReceivablesBalance(cart: OrderQuoteCartInput): number {
  const financials = getOrderQuoteFinancials(cart)
  const quoteBalance = financials.remainingTotal ?? financials.total

  if (quoteBalance !== null) {
    return quoteBalance
  }

  if (hasPendingOrderQuoteTotals(cart)) {
    const pendingFinancials = getOrderQuote(cart)?.pending_financials

    return (
      parseOrderQuoteMoney(
        isRecord(pendingFinancials) ? pendingFinancials.receivables_balance : null
      ) ?? 0
    )
  }

  return 0
}

export function warnMissingOrderQuoteFinancials(
  cart: OrderQuoteCartInput,
  fields: Array<keyof OrderQuoteFinancials>,
  message: string
): void {
  if (!cart) {
    return
  }

  if (hasPendingOrderQuoteTotals(cart)) {
    return
  }

  const missingFields = fields.filter((field) => getOrderQuoteFinancials(cart)[field] == null)

  if (missingFields.length === 0) {
    return
  }

  warnOrderQuoteContractIssue(message, {
    cartId: cart?.id ?? null,
    fields: missingFields.join(','),
  })
}
